import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Activity, ShieldCheck, AlertTriangle, MinusCircle, CheckCircle2, RefreshCw,
} from "lucide-react";
import { api } from "@/lib/api";

// Color tokens for each status (mirror the backup health pill so the
// admin UI feels uniform across surfaces).
const STATUS_STYLE = {
    healthy: {
        ring: "border-[var(--bl-moss)]/30 bg-[var(--bl-moss-tint)]",
        text: "text-[var(--bl-moss)]",
        dot: "bg-[var(--bl-moss)]",
        Icon: ShieldCheck,
    },
    stale: {
        ring: "border-[var(--bl-amber)]/40 bg-[var(--bl-amber-tint)]",
        text: "text-[var(--bl-amber)]",
        dot: "bg-[var(--bl-amber)]",
        Icon: AlertTriangle,
    },
    red: {
        ring: "border-[var(--bl-rust)]/40 bg-[var(--bl-rust-tint)]",
        text: "text-[var(--bl-rust)]",
        dot: "bg-[var(--bl-rust)]",
        Icon: AlertTriangle,
    },
    idle: {
        ring: "border-bl bg-bl-bg",
        text: "text-bl-muted",
        dot: "bg-bl-muted",
        Icon: MinusCircle,
    },
    unknown: {
        ring: "border-bl bg-bl-bg",
        text: "text-bl-muted",
        dot: "bg-bl-muted",
        Icon: MinusCircle,
    },
};

function formatWhen(iso) {
    if (!iso) return "—";
    try {
        const ts = new Date(iso);
        const diff = (Date.now() - ts.getTime()) / 1000;
        if (diff < 60) return `${Math.floor(diff)}s ago`;
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return `${Math.floor(diff / 86400)}d ago`;
    } catch {
        return iso;
    }
}

export default function HealthTripwiresPanel() {
    const [tripwires, setTripwires] = useState([]);
    const [loading, setLoading] = useState(true);
    const [runningId, setRunningId] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await api.get("/admin/ops/health-tripwires");
            setTripwires(r.data?.tripwires || []);
        } catch (e) {
            toast.error(`Health load failed: ${e.response?.data?.detail || e.message}`);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const runNow = async (id) => {
        setRunningId(id);
        try {
            const r = await api.post(`/admin/ops/health-tripwires/${id}/run-now`);
            toast.success(`Ran ${id} · ${r.data?.status || "ok"}`);
            await load();
        } catch (e) {
            toast.error(`Run failed: ${e.response?.data?.detail || e.message}`);
        } finally {
            setRunningId(null);
        }
    };

    // Aggregate top-of-card summary — green when every tripwire is healthy,
    // amber if any are stale/idle, red if any are red. Gives the admin
    // a 2-second "everything ok?" gut check before they read details.
    const allHealthy = tripwires.length > 0 && tripwires.every((t) => t.status === "healthy");
    const anyRed = tripwires.some((t) => t.status === "red");
    const summaryStatus = anyRed ? "red" : (allHealthy ? "healthy" : "stale");
    const summaryStyle = STATUS_STYLE[summaryStatus];

    return (
        <section
            className="bl-card p-5 sm:p-6"
            data-testid="admin-health-tripwires-panel"
        >
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-2">
                    <Activity className="w-4 h-4 text-bl-muted mt-1 shrink-0" aria-hidden />
                    <div>
                        <h3 className="font-display font-semibold text-base text-bl">
                            Health tripwires
                        </h3>
                        <p className="text-[11px] text-bl-muted mt-0.5">
                            Three state-transition canaries. You get one email per status flip — never spam.
                        </p>
                    </div>
                </div>
                {tripwires.length > 0 && (
                    <span
                        className={
                            "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] tracking-[0.16em] uppercase font-medium border " +
                            summaryStyle.ring + " " + summaryStyle.text
                        }
                        data-testid={`admin-health-summary-${summaryStatus}`}
                    >
                        <span className={"w-1.5 h-1.5 rounded-full " + summaryStyle.dot} />
                        {summaryStatus === "healthy" && "All systems green"}
                        {summaryStatus === "stale" && "Mixed signals"}
                        {summaryStatus === "red" && "Attention needed"}
                    </span>
                )}
            </div>

            {loading && (
                <div className="mt-4 text-sm text-bl-muted" data-testid="health-loading">
                    Loading…
                </div>
            )}

            {!loading && tripwires.length === 0 && (
                <div className="mt-4 text-sm text-bl-muted" data-testid="health-empty">
                    No tripwires registered.
                </div>
            )}

            {!loading && tripwires.length > 0 && (
                <ul
                    className="mt-4 divide-y divide-bl border border-bl rounded-xl overflow-hidden"
                    data-testid="health-tripwires-list"
                >
                    {tripwires.map((t) => {
                        const style = STATUS_STYLE[t.status] || STATUS_STYLE.unknown;
                        const Icon = style.Icon;
                        return (
                            <li
                                key={t.id}
                                className="px-4 sm:px-5 py-3 bg-white"
                                data-testid={`health-row-${t.id}`}
                            >
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="flex items-start gap-3 min-w-0 flex-1">
                                        <Icon className={"w-4 h-4 mt-0.5 shrink-0 " + style.text} />
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-sm font-medium text-bl">
                                                    {t.label}
                                                </span>
                                                <span
                                                    className={
                                                        "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] tracking-[0.14em] uppercase font-medium border " +
                                                        style.ring + " " + style.text
                                                    }
                                                    data-testid={`health-status-${t.id}-${t.status}`}
                                                >
                                                    <span className={"w-1 h-1 rounded-full " + style.dot} />
                                                    {t.status}
                                                </span>
                                                {t.last_transition_kind && (
                                                    <span className="inline-flex items-center gap-1 text-[10px] text-bl-muted">
                                                        {t.last_transition_kind === "regression" ? (
                                                            <AlertTriangle className="w-3 h-3" />
                                                        ) : (
                                                            <CheckCircle2 className="w-3 h-3" />
                                                        )}
                                                        last flip {formatWhen(t.last_transition_at)}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-0.5 text-[12px] text-bl">
                                                {t.message || t.description}
                                            </div>
                                            <div className="mt-0.5 text-[10px] text-bl-muted">
                                                {t.description}
                                                {" · "}checked {formatWhen(t.checked_at)}
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => runNow(t.id)}
                                        disabled={runningId === t.id}
                                        className="bl-btn-secondary text-xs gap-1.5 py-1.5 px-3 disabled:opacity-60"
                                        data-testid={`health-run-now-${t.id}`}
                                    >
                                        <RefreshCw className={
                                            "w-3.5 h-3.5 " + (runningId === t.id ? "animate-spin" : "")
                                        } />
                                        {runningId === t.id ? "Running…" : "Run now"}
                                    </button>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
}
